<template>
    <div data-cy="voc-item-picker">
        <pendo-multiselect
            ref="vocItemPicker"
            class="voc-item-picker"
            :class="{ 'voc-item-picker--hideBar': searchText === '' }"
            :full-width="true"
            :placeholder="vocItemType === 'idea' ? 'Search ideas' : 'Search feedback'"
            :options="filteredVocItemsAsOptions"
            :option-height="156"
            :loading="searching"
            :disable-while-loading="false"
            :searchable="true"
            :internal-search="false"
            :max-menu-height="388"
            :popper-options="{ class: 'voc-item-picker--dropdown' }"
            @select="addItem"
            @input-value-change="inputValueChange"
            @click-outside="clearVocItems">
            <template #option="{ option }">
                <voc-item-card
                    type="option"
                    disable-account-hover
                    :item="option"
                    size="small"
                    :actions="actions"
                    @action="handleAction" />
            </template>
            <template
                #options
                v-if="filteredVocItemsAsOptions.length === 0 || searchText === '' || searching || showErrorState">
                <div
                    v-if="searchText === ''"
                    class="search-results__startTyping">
                    Please start typing to search {{ vocItemType === 'idea' ? 'ideas' : 'feedback' }}
                </div>
                <div v-else-if="searching">
                    <pendo-loading-indicator
                        class="search-results__loading"
                        size="small" />
                </div>
                <div
                    v-else-if="showErrorState"
                    class="search-results__error">
                    <img
                        src="@img/voc/horizon-search-error.svg"
                        height="60"
                        width="60">
                    <div class="search-results__errorText">
                        Oh no, search is not currently available, please try again later.
                    </div>
                </div>
                <div
                    v-else
                    class="search-results__noResults"
                    data-cy="discover-search-no-results">
                    <img
                        src="@img/voc/no-search-results.svg"
                        height="60"
                        width="60">
                    <div class="search-results__noResultsText">
                        No results matched "{{ searchText }}"
                        <span v-if="vocItemType === 'feedback'"> for "feedback"</span>
                        <span v-if="vocItemType === 'idea'"> for "ideas"</span>
                    </div>
                </div>
            </template>
            <template #footer="{ toggleMenu }">
                <div
                    v-if="amountOfResults > maxResults && !searching"
                    class="search-results__maxResultMessage">
                    Showing {{ maxResults }} results out of a total of {{ amountOfResults }}
                </div>
                <div
                    v-if="showCreateButton"
                    class="create-button__container">
                    <pendo-button
                        class="create-button"
                        :label="'Create new ' + vocItemType"
                        type="secondary"
                        theme="app"
                        @click="openForm(toggleMenu)" />
                </div>
            </template>
        </pendo-multiselect>
        <div
            v-if="!hideSelected"
            class="selected-items"
            data-cy="linked-ideas">
            <transition-group name="slider">
                <voc-item-card
                    v-for="vocItem in selectedItems"
                    :key="vocItem.id"
                    has-border
                    :item="vocItem"
                    :actions="linkedItemsActions"
                    @action="handleAction" />
            </transition-group>
        </div>
    </div>
</template>

<script>
import LOADING_STATES from '@/constants/loading-states.js';
import VocItemsSearchAggregation from '@/voc/aggregations/voc-items-search/voc-items-search.aggregation.js';
import VocItemCard from '@/voc/components/voc-item-card/voc-item-card.vue';
import { connectAggregationToState, withComponent } from '@pendo/agg-connect';
import { PendoButton, PendoLoadingIndicator, PendoMultiselect, PendoTooltip } from '@pendo/components';
import { EventBus } from '@utils/event-bus.js';
import debounce from 'lodash/debounce';
import { mapActions, mapGetters, mapState } from 'vuex';

import { hasSetupExplore } from '@/voc/helpers/permissions.helper';

const DEBOUNCE_TIMEOUT = 400;
const { LOADING, RESOLVED, REJECTED } = LOADING_STATES;

export default {
    name: 'VocItemPicker',
    components: {
        VocItemCard,
        PendoButton,
        PendoMultiselect,
        PendoLoadingIndicator,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        selectedItems: {
            type: Array,
            default: () => [],
        },
        vocItemType: {
            type: String,
            required: true,
        },
        allowCreate: {
            type: Boolean,
            default: false,
        },
        alreadyLinkedIds: {
            type: Array,
            default: () => [],
        },
        hideSelected: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['select-item', 'remove-item', 'openFeedbackForm', 'openItem', 'openIdeaForm'],
    data () {
        return {
            vocItemsAsOptions: [],
            searching: false,
            searchText: '',
            amountOfResults: 0,
            maxResults: 100,
            showErrorState: false,
            actions: [
                {
                    id: 'view',
                    icon: 'external-link',
                    label: this.$t('common.View'),
                },
            ],
            linkedItemsActions: [
                {
                    id: 'view',
                    icon: 'external-link',
                    label: this.$t('common.View'),
                },
                {
                    id: 'remove',
                    icon: 'x',
                    label: this.$t('common.Remove'),
                },
            ],
            fetchInsights: this.vocItemType === 'feedback' && hasSetupExplore(),
        };
    },
    computed: {
        ...mapGetters({
            canSubmitFeedback: 'vocItems/canSubmitFeedback',
            canEditVocItems: 'vocItems/canEditVocItems',

        }),
        ...mapState({
            accountDomain: (state) => state.subscription?.settings?.accountDomain?.value || null,
            accountDisplayName: (state) => state.subscription?.settings?.accountDisplayName?.value || null,
        }),
        filteredVocItemsAsOptions () {
            return this.vocItemsAsOptions.filter((item) => {
                return (
                    !this.selectedItems.some((removeItem) => removeItem.id === item.id) &&
                    !this.alreadyLinkedIds.some((id) => id === item.id)
                );
            });
        },
        showCreateButton () {
            let hasPermission = false;
            if (this.vocItemType === 'feedback') {
                hasPermission = this.canSubmitFeedback;
            } else {
                hasPermission = this.canEditVocItems;
            }

            return this.allowCreate && hasPermission && (!this.searching || this.searchText === '');
        },
    },
    watch: {
        searchText: debounce(function (value) {
            if (value === '') {
                return;
            }

            this.setupVocItemSearchStream();
        }, DEBOUNCE_TIMEOUT),
    },
    created () {
        EventBus.$on('triggerLinkedIdeasMultiselect', this.triggerPicker);
    },
    unmounted () {
        EventBus.$off('triggerLinkedIdeasMultiselect', this.triggerPicker);
    },
    methods: {
        ...mapActions({
            loadSetting: 'subscription/loadSetting',
        }),
        handleAction ({ action, item }) {
            switch (action.id) {
                case 'view':
                    this.$emit('openItem', item);
                    break;
                case 'remove':
                    this.$emit('remove-item', item);
                    break;
            }
        },
        addItem (item) {
            this.$emit('select-item', item);
            this.clearVocItems();
        },
        inputValueChange (value) {
            if (this.agg$) this.agg$.unsubscribe();
            this.searchText = value;
            this.showErrorState = false;
            if (value === '') {
                this.clearVocItems();

                return;
            }
            this.searching = true;
        },
        clearVocItems () {
            if (this.agg$) this.agg$.unsubscribe();
            this.searchText = '';
            this.amountOfResults = 0;
            this.vocItemsAsOptions = [];
            this.searching = false;
        },
        openForm (toggleMenu) {
            toggleMenu();
            if (this.vocItemType === 'feedback') {
                this.$emit('openFeedbackForm');
            } else {
                this.$emit('openIdeaForm');
            }
        },
        triggerPicker () {
            this.$refs.vocItemPicker.$refs.core.activate();
        },
        async setupVocItemSearchStream () {
            if (this.agg$) this.agg$.unsubscribe();
            await Promise.all([
                ...(['accountDomain', 'accountDisplayName'].map((setting) =>
                    this.loadSetting({ setting, retrieveMetadata: false }),
                )),
            ]);
            const vocItemSearchInitAgg$ = this.initVocItemSearchAgg();
            this.agg$ = vocItemSearchInitAgg$.subscribe(this.onAggResponse);
        },
        initVocItemSearchAgg () {
            const sourcesOfState = {
                component: this,
            };

            return connectAggregationToState(
                VocItemsSearchAggregation,
                withComponent({
                    type: 'vocItemType',
                    searchText: 'searchText',
                    accountDisplayName: 'accountDisplayName',
                    accountDomain: 'accountDomain',
                    includeInsights: 'fetchInsights',
                }),
            )(sourcesOfState);
        },
        onAggResponse ({ status, value }) {
            switch (status) {
                case RESOLVED:
                    this.amountOfResults = value.length;
                    this.vocItemsAsOptions = value.splice(0, this.maxResults);
                    this.searching = false;
                    break;
                case LOADING:
                    this.searching = true;
                    this.showErrorState = false;
                    break;
                case REJECTED:
                    this.showErrorState = true;
                    this.searching = false;
                    break;
            }
        },
    },
};
</script>

<style lang="less" scoped>
.selected-items>span {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
}

.search-results {
    &__noResults,
    &__error {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 3px;
        padding: 14px;
    }

    &__noResultsText,
    &__errorText {
        font-weight: 400;
        line-height: 130%;
        color: var(--palette-gray-70);
        text-align: center;
        width: 70%;
        margin-top: 4px;
    }

    &__loading {
        padding: 14px;
    }

    &__maxResultMessage,
    &__startTyping {
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 400;
        padding: 8px;
        color: var(--palette-gray-70);
    }

    &__startTyping {
        margin: 12px;
    }

    &__maxResultMessage {
        padding: 14px;
    }
}

.create-button__container {
    display: flex;
    justify-content: flex-end;
    width: 100%;
}

.create-button {
    margin: 10px;
}

.slider-enter-active {
    overflow: hidden;
    transition: all 0.3s ease-out;
}

.slider-leave-active {
    overflow: hidden;
    transition: all 0.3s ease-out;
}

.slider-leave-to {
    transform: translateX(50px);
    opacity: 0;
}

.slider-enter {
    transform: translateX(50px);
    opacity: 0;
}

.search-results__startTyping + .pendo-multiselect__footer .create-button,
.search-results__noResults + .pendo-multiselect__footer .create-button,
.search-results__error + .pendo-multiselect__footer .create-button,
.search-results__maxResultMessage + .create-button__container .create-button {
    margin-top: 0;
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="less">
.voc-item-picker--dropdown {
    .pendo-multiselect__content-wrapper {
        padding: 4px;
    }
    .pendo-multiselect__option {
        border: 1px solid var(--palette-gray-30);
        margin: 4px;
        padding: 0;
    }
}
</style>
